// Live intra-stroke speed trace for Check Factor (on the water, not Erg).
// Case GPS (10 Hz) speed is used directly. Phone: speed = smoothed GPS speed +
// leaky integral of forward acceleration (mean removed), sampled at 20 Hz.
import { useEffect, useRef, useState } from 'react';
import { computeCheckFactor } from '@/lib/checkFactor';

const SAMPLE_MS = 50;
const MAX_POINTS = 100;
const CASE_FRESH_MS = 1500;

export default function useCheckFactorFeed({ enabled, baseSpeed }) {
  const [data, setData] = useState([]);
  const bufRef = useRef([]);
  const baseRef = useRef(baseSpeed);
  baseRef.current = baseSpeed;

  useEffect(() => {
    bufRef.current = [];
    setData([]);
    if (!enabled) return undefined;
    let vVar = 0; let aMean = 0; let lastT = 0; let lastPush = 0; let lastCase = 0;
    const push = (t, speed) => {
      if (t - lastPush < SAMPLE_MS || !(speed >= 0)) return;
      lastPush = t;
      bufRef.current = [...bufRef.current, { timestamp: t, speed }].slice(-MAX_POINTS);
    };
    const onAccel = (e) => {
      const { t, a, source } = e.detail || {};
      if (source !== 'phone' || !Number.isFinite(a)) return;
      const dt = lastT ? Math.min(0.1, (t - lastT) / 1000) : 0;
      lastT = t;
      aMean += (a - aMean) * 0.01;
      vVar = (vVar + (a - aMean) * dt) * 0.98;
      if (t - lastCase > CASE_FRESH_MS && baseRef.current > 0.5) push(t, baseRef.current + vVar);
    };
    const onCase = (e) => {
      const d = e.detail || {};
      if (typeof d.speed !== 'number') return;
      lastCase = Date.now();
      push(lastCase, d.speed);
    };
    window.addEventListener('crewz-accel-raw', onAccel);
    window.addEventListener('crewz-gps-update', onCase);
    const id = setInterval(() => {
      setData(bufRef.current);
      const r = computeCheckFactor(bufRef.current);
      if (r) window.dispatchEvent(new CustomEvent('crewz-cf-update', { detail: { cfPct: r.cf } }));
    }, 1000);
    return () => {
      window.removeEventListener('crewz-accel-raw', onAccel);
      window.removeEventListener('crewz-gps-update', onCase);
      clearInterval(id);
    };
  }, [enabled]);

  return data;
}
